/**
 * OnboardingScreen - Tela de boas-vindas do UniQuest
 *
 * Tela única (substituiu os 3 slides deslizáveis herdados do QuizFlow em
 * 2026-08-05). É a PORTA DE ENTRADA de quem está deslogado: aparece em toda
 * abertura sem sessão e depois do logout. Quem tem sessão vai direto para Home
 * e nunca vê esta tela.
 *
 * ⚠️ CONTRATO COM O BOOT — não quebrar:
 *  - Quem decide a rota inicial é `decideInitialRoute` no App.js (leitura de
 *    `supabase.auth.getSession()`). Esta tela NÃO guarda mais flag de "já vi" —
 *    a sessão é a única fonte de verdade.
 *  - A rota 'Onboarding' precisa continuar em PUBLIC_ROUTES (App.js), senão o
 *    guard de foco chuta o usuário deslogado para UserAuth — e como Home é
 *    gated, isso viraria um laço.
 *  - Os botões navegam para 'UserAuth' com `mode`, que UserAuthScreen lê em
 *    `normalizeInitialTab` (UserAuthScreen.js ~l.34).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Animated,
    AppState,
    Easing,
    Platform,
    Pressable,
    StatusBar,
    StyleSheet,
    useWindowDimensions,
    View,
} from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Text } from '../components/ui/AppText';

// React Query - aquece o ranking enquanto o usuário lê a tela.
import { queryClient } from '../providers/QueryProvider';
import { rankingKeys } from '../hooks/queries/useRankingQuery';
import rankingService from '../services/rankingService';

const COLORS = {
    navy: '#0B3D75',
    navyDark: '#09305C',
    yellow: '#F5C518',
    ink: '#0A3768',
    white: '#FFFFFF',
    muted: 'rgba(255,255,255,0.62)',
    pillBase: '#D9D9D9',
};

// Ajuste exclusivo da versão web (ver `bodyWeb` lá embaixo). Resolvido uma vez,
// no load do módulo: em nativo `IS_WEB` é `false` e todo estilo web vira `false`
// dentro dos arrays de estilo — o RN ignora. O visual do app não muda.
const IS_WEB = Platform.OS === 'web';
// `w(web, nativo)` — devolve o valor nativo em app, o compacto na web.
// A altura que a faixa de fotos deixou de ceder (ela agora tem tamanho fixo por
// regra) sai daqui: dos espaçamentos e corpos de fonte dos blocos DE BAIXO.
const w = (web, nativo) => (IS_WEB ? web : nativo);

const FONTS = {
    light: 'Liebling-Light',
    regular: 'Liebling',
    medium: 'Liebling-Medium',
    bold: 'Liebling-Bold',
    heavy: 'Liebling-Heavy',
};

// Ampliação sobre a geometria original do Figma. TODA a faixa de fotos (largura,
// altura, raio, backdrop e o recorte de cada foto) é derivada daqui — mexer só
// neste número mantém a proporção. 1 = tamanho transcrito do Figma.
//
// ⚠️ Os assets em assets/onboarding/ foram exportados para ~3× o tamanho exibido
// em PILL_SCALE = 1. Subir muito acima de ~1.5 começa a esticar bitmap.
const PILL_SCALE = 1.22;
const s = (n) => n * PILL_SCALE;

const PILL_W = s(88);
const PILL_GAP = s(22);
const STRIP_H = s(277);
const PILL_RADIUS = s(55.7);
// Alcance da sombra além da cápsula (offset + raio). A faixa precisa reservar
// esse espaço em cima e embaixo, senão o `overflow: 'hidden'` dela — que existe
// para o loop horizontal funcionar — decapita o borrão junto.
const SHADOW_PAD = s(28);

// Altura total da faixa, sombra inclusa. Extraída para constante porque o ajuste
// responsivo da web (ver PhotoMarquee) precisa da razão entre a altura que o
// flex concedeu e esta aqui.
const STRIP_TOTAL = STRIP_H + SHADOW_PAD * 2;

// ⚠️ REGRA DE PRODUTO: em celulares, o carrossel deve caber 4 cápsulas na
// largura. Em aparelhos estreitos elas diminuem; como altura e largura usam a
// mesma escala, isso também reduz a altura do carrossel.
const MAX_PILLS_VISIBLE = 4;
const MIN_PILL_RESPONSIVE_SCALE = 0.58;
const MAX_PILL_RESPONSIVE_SCALE = 1;

// Geometria transcrita do Figma e escalada por 0.398 (frame de 221px → 88px).
// Os raios assimétricos e os offsets negativos são intencionais — não arredondar.
// Valores BASE: o fator PILL_SCALE é aplicado logo abaixo, de uma vez só.
const BASE_PILLS = [
    {
        key: 'purple',
        height: 266,
        top: 0,
        backdrop: { color: '#8644A2', left: -23.6, top: 0, width: 168.1, height: 265.8 },
        image: { src: require('../../assets/onboarding/photo-purple.webp'), left: -154.8, top: 31.8, width: 333.2, height: 234.1 },
    },
    {
        key: 'hijab',
        height: 178,
        top: 52,
        image: { src: require('../../assets/onboarding/student-hijab.webp'), left: -74.4, top: 0, width: 252.3, height: 191.4 },
    },
    {
        key: 'orange',
        height: 277,
        top: 0,
        backdrop: { color: '#FF8A00', left: -39.8, top: -0.4, width: 168.4, height: 277.4 },
        image: { src: require('../../assets/onboarding/photo-orange.webp'), left: -43, top: 0, width: 174.6, height: 281.5 },
    },
    {
        key: 'pink',
        height: 214,
        top: 26,
        backdrop: { color: '#FF0080', left: -34.2, top: 0, width: 168.1, height: 252.2 },
        image: { src: require('../../assets/onboarding/photo-pink.webp'), left: -28.7, top: 13.5, width: 159.6, height: 240.1 },
    },
    {
        key: 'cream',
        height: 196,
        top: 68,
        backdrop: { color: '#ECE5D5', left: -47.8, top: -35.8, width: 168.1, height: 310 },
        image: { src: require('../../assets/onboarding/photo-cream.webp'), left: -47.8, top: 21.5, width: 168.1, height: 252.2 },
    },
    {
        key: 'green',
        height: 240,
        top: 12,
        backdrop: { color: '#4CCD99', left: -39.7, top: -4.9, width: 168.1, height: 252.2 },
        image: { src: require('../../assets/onboarding/photo-green.webp'), left: -39.8, top: -5.2, width: 168.3, height: 252.4 },
    },
];

// Aplica PILL_SCALE em bloco. Escalar aqui (e não em cada uso) garante que
// backdrop e foto continuem alinhados com a pílula — eles dependem de offsets
// negativos, então escalar só uma parte desalinharia o recorte.
const scaleBox = (box) => ({
    ...box,
    left: s(box.left),
    top: s(box.top),
    width: s(box.width),
    height: s(box.height),
});

const PILLS = BASE_PILLS.map((pill) => ({
    ...pill,
    height: s(pill.height),
    top: s(pill.top),
    backdrop: pill.backdrop ? scaleBox(pill.backdrop) : undefined,
    image: scaleBox(pill.image),
}));

// Substituiu o seletor "Choose your Category" do mock. É informativo (sem
// estado), espelhando a estrutura real da prova do ENADE + o que o app entrega.
const HIGHLIGHTS = [
    { key: 'geral', icon: 'earth-outline', title: 'Formação Geral', compactTitle: 'Geral', caption: 'Atualidades, ética e leitura crítica' },
    { key: 'especifico', icon: 'school-outline', title: 'Componente Específico', compactTitle: 'Específico', caption: 'Questões do seu curso' },
    { key: 'desempenho', icon: 'stats-chart-outline', title: 'Desempenho', compactTitle: 'Evolução', caption: 'Simulados e evolução' },
];

/**
 * Duas Views aninhadas de propósito:
 *  - a EXTERNA carrega a sombra;
 *  - a INTERNA carrega o `overflow: 'hidden'` que recorta a foto na cápsula.
 *
 * Não dá para juntar as duas: no iOS `overflow: 'hidden'` vira `masksToBounds`,
 * que recorta a sombra da própria view junto com o conteúdo — a sombra
 * simplesmente sumiria. A externa também precisa de `backgroundColor` opaco,
 * senão o `elevation` do Android não tem outline para projetar.
 */
function Pill({ pill, shimmer, onLoaded, metrics }) {
    // WEB — shimmer de carregamento. No app os assets vêm empacotados e a foto
    // aparece instantânea; na web são 6 requisições HTTP (188 KB), e com
    // `transition={0}` a imagem ESTALA na tela quando chega. O shimmer cobre
    // essa janela.
    //
    // O efeito é uma faixa translúcida varrendo a cápsula de cima para baixo —
    // vertical porque a cápsula é alta e estreita (~107×338), formato em que uma
    // varredura horizontal mal apareceria. Ela passa POR CIMA do que já está
    // desenhado (o `backdrop` colorido), em vez de tapar com cinza: assim a cor
    // da cápsula continua visível durante o carregamento.
    //
    // `onLoadEnd` (e não `onLoad`) de propósito: dispara também em erro de rede,
    // senão uma imagem que falhasse deixaria a cápsula piscando para sempre.
    // Em nativo `shimmer` chega null → `onLoadEnd` vira undefined (idêntico a não
    // passar prop) e o overlay não é renderizado. A árvore nativa não muda.
    const [done, setDone] = useState(false);
    const overlay = useRef(new Animated.Value(1)).current;

    const handleLoadEnd = useCallback(() => {
        Animated.timing(overlay, {
            toValue: 0,
            duration: 320,
            easing: Easing.out(Easing.quad),
            // O driver nativo nao existe no React Native Web. Pedir o driver
            // ali apenas gera warning e faz o Animated refazer a escolha em JS.
            useNativeDriver: !IS_WEB,
        }).start(() => setDone(true));
        onLoaded?.();
    }, [overlay, onLoaded]);

    // Altura da faixa de brilho: metade da cápsula deixa o rastro legível sem
    // clarear a peça inteira de uma vez.
    const band = pill.height * 0.5;

    return (
        <View
            style={[
                styles.pillShadow,
                {
                    width: metrics.pillW,
                    height: pill.height,
                    marginTop: pill.top,
                    borderRadius: metrics.pillRadius,
                    shadowOffset: { width: 0, height: metrics.shadowOffsetY },
                    shadowRadius: metrics.shadowRadius,
                },
            ]}
        >
            <View style={[styles.pillClip, { borderRadius: metrics.pillRadius }]}>
                {pill.backdrop ? (
                    <View
                        style={{
                            position: 'absolute',
                            left: pill.backdrop.left,
                            top: pill.backdrop.top,
                            width: pill.backdrop.width,
                            height: pill.backdrop.height,
                            backgroundColor: pill.backdrop.color,
                        }}
                    />
                ) : null}
                <Image
                    source={pill.image.src}
                    contentFit="cover"
                    transition={0}
                    onLoadEnd={shimmer ? handleLoadEnd : undefined}
                    style={{
                        position: 'absolute',
                        left: pill.image.left,
                        top: pill.image.top,
                        width: pill.image.width,
                        height: pill.image.height,
                    }}
                />
                {shimmer && !done ? (
                    <Animated.View
                        pointerEvents="none"
                        style={[StyleSheet.absoluteFill, { opacity: overlay }]}
                    >
                        <Animated.View
                            style={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                height: band,
                                transform: [
                                    {
                                        translateY: shimmer.interpolate({
                                            inputRange: [0, 1],
                                            outputRange: [-band, pill.height],
                                            // sem clamp, um valor fora de [0,1]
                                            // joga a faixa para fora da cápsula
                                            extrapolate: 'clamp',
                                        }),
                                    },
                                ],
                            }}
                        >
                            <LinearGradient
                                colors={['rgba(255,255,255,0)', 'rgba(255,255,255,0.38)', 'rgba(255,255,255,0)']}
                                style={{ flex: 1 }}
                            />
                        </Animated.View>
                    </Animated.View>
                ) : null}
            </View>
        </View>
    );
}

/**
 * Carrossel decorativo. A lista é duplicada e o deslocamento reinicia a cada
 * ciclo calculado, dando a ilusão de loop infinito.
 *
 * A animação PARA quando a tela perde foco ou o app vai para background — sem
 * isso o `Animated.loop` roda para sempre queimando bateria (o mock original
 * não tratava isso).
 */
function PhotoMarquee({ speed = 26 }) {
    const x = useRef(new Animated.Value(0)).current;
    const loopRef = useRef(null);
    // UM único valor animado alimenta o shimmer das 12 cápsulas (a lista é
    // duplicada para o loop). Uma animação em vez de doze — e, de brinde, as
    // varreduras ficam em sincronia, o que lê como intenção de design em vez de
    // ruído. Ele entra e sai junto com o marquee (start/stop abaixo), herdando a
    // proteção de bateria que já existia: nada anima com a tela fora de foco.
    const { width: windowWidth } = useWindowDimensions();
    const shimmer = useRef(new Animated.Value(0)).current;
    const shimmerLoopRef = useRef(null);
    const metrics = useMemo(() => {
        const baseTrackPaddingLeft = 22;
        const fourPillWidth =
            baseTrackPaddingLeft
            + MAX_PILLS_VISIBLE * PILL_W
            + (MAX_PILLS_VISIBLE - 1) * PILL_GAP;
        const responsiveScale = Math.min(
            MAX_PILL_RESPONSIVE_SCALE,
            Math.max(MIN_PILL_RESPONSIVE_SCALE, windowWidth / fourPillWidth)
        );

        return {
            scale: responsiveScale,
            pillW: PILL_W * responsiveScale,
            pillGap: PILL_GAP * responsiveScale,
            pillRadius: PILL_RADIUS * responsiveScale,
            stripTotal: STRIP_TOTAL * responsiveScale,
            shadowOffsetY: s(10) * responsiveScale,
            shadowRadius: s(18) * responsiveScale,
            trackPaddingLeft: baseTrackPaddingLeft * responsiveScale,
            trackPaddingTop: SHADOW_PAD * responsiveScale,
            viewportMaxWidth: fourPillWidth * responsiveScale,
        };
    }, [windowWidth]);
    const displayPills = useMemo(
        () =>
            PILLS.map((pill) => ({
                ...pill,
                height: pill.height * metrics.scale,
                top: pill.top * metrics.scale,
                backdrop: pill.backdrop
                    ? {
                        ...pill.backdrop,
                        left: pill.backdrop.left * metrics.scale,
                        top: pill.backdrop.top * metrics.scale,
                        width: pill.backdrop.width * metrics.scale,
                        height: pill.backdrop.height * metrics.scale,
                    }
                    : undefined,
                image: {
                    ...pill.image,
                    left: pill.image.left * metrics.scale,
                    top: pill.image.top * metrics.scale,
                    width: pill.image.width * metrics.scale,
                    height: pill.image.height * metrics.scale,
                },
            })),
        [metrics.scale]
    );
    const cycle = displayPills.length * (metrics.pillW + metrics.pillGap);
    // Quantas cápsulas ainda não carregaram. Zera → o loop do shimmer para, em
    // vez de girar para sempre atrás de overlays já invisíveis.
    const [pending, setPending] = useState(IS_WEB ? PILLS.length * 2 : 0);
    const notePillLoaded = useCallback(() => setPending((n) => (n > 0 ? n - 1 : 0)), []);

    const start = useCallback(() => {
        if (loopRef.current) return;
        // Rebobina antes de (re)começar. `Animated.loop` reseta cada iteração
        // para o valor em que o LOOP começou — se ele fosse retomado no meio do
        // ciclo (volta do background), passaria a percorrer só o trecho restante
        // e saltaria a cada volta. Como a lista é duplicada, o offset 0 é
        // visualmente idêntico ao fim do ciclo, então rebobinar não cria emenda.
        x.setValue(0);
        loopRef.current = Animated.loop(
            Animated.timing(x, {
                toValue: -cycle,
                duration: (cycle / speed) * 1000,
                easing: Easing.linear,
                // Nativo continua com driver nativo (comportamento do app
                // inalterado). Na WEB precisa ser false: não existe driver
                // nativo no navegador (o RNW avisa e cai para JS), e nesse
                // caminho o `Animated.loop` roda UMA iteração e para.
                // Medido no build publicado: o carrossel andava até t+32s
                // (= uma volta) e congelava em -805.2 para sempre.
                // O bug não aparecia em teste curto porque uma volta leva ~31s.
                useNativeDriver: !IS_WEB,
                isInteraction: false, // loop infinito NAO e interacao do usuario
            })
        );
        loopRef.current.start();
    }, [cycle, speed, x]);

    const stop = useCallback(() => {
        loopRef.current?.stop();
        loopRef.current = null;
    }, []);

    // Loop do shimmer: efeito PRÓPRIO, montado uma única vez. Ele não pode viver
    // dentro do `start()` do marquee porque aquela função começa com
    // `if (loopRef.current) return;` — guarda que existe para o marquee não ser
    // reiniciado, e que fazia o shimmer nunca chegar a ser criado nas chamadas
    // subsequentes de start(). Medido: o track animava e a faixa ficava parada.
    useEffect(() => {
        if (!IS_WEB) return undefined;
        // `useNativeDriver: false` DE PROPÓSITO — e é o detalhe que fazia o
        // shimmer não funcionar. Na web não existe driver nativo (o próprio RNW
        // avisa no console e cai para JS), e nesse caminho o `Animated.loop`
        // executa UMA iteração e para. Medido: o translateY das 12 faixas ficava
        // congelado exatamente em `pill.height`, o fim do outputRange.
        // Pedir o driver nativo aqui não traz ganho nenhum (não há nativo na web)
        // e custa o loop. Ver a nota sobre o marquee no decision-log.
        const loop = Animated.loop(
            Animated.timing(shimmer, {
                toValue: 1,
                duration: 1400,
                easing: Easing.linear,
                useNativeDriver: false,
                isInteraction: false, // loop infinito NAO e interacao do usuario
            })
        );
        shimmerLoopRef.current = loop;
        loop.start();
        return () => {
            loop.stop();
            shimmerLoopRef.current = null;
        };
    }, [shimmer]);

    // Todas carregaram → encerra o loop.
    useEffect(() => {
        if (IS_WEB && pending === 0) {
            shimmerLoopRef.current?.stop();
            shimmerLoopRef.current = null;
        }
    }, [pending]);

    // Foco da rota: roda só enquanto a tela está visível.
    useFocusEffect(
        useCallback(() => {
            start();
            return stop;
        }, [start, stop])
    );

    // Background do app: o foco da rota não muda ao minimizar, então precisa
    // deste segundo gatilho.
    useEffect(() => {
        const sub = AppState.addEventListener('change', (state) => {
            if (state === 'active') start();
            else stop();
        });
        return () => sub.remove();
    }, [start, stop]);

    return (
        <View
            style={[
                styles.strip,
                {
                    height: metrics.stripTotal,
                    minHeight: 0,
                    flexShrink: 0,
                    width: '100%',
                    maxWidth: metrics.viewportMaxWidth,
                    alignSelf: 'center',
                },
            ]}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
        >
            <Animated.View
                style={[
                    styles.track,
                    {
                        gap: metrics.pillGap,
                        paddingLeft: metrics.trackPaddingLeft,
                        paddingTop: metrics.trackPaddingTop,
                        transform: [{ translateX: x }],
                    },
                ]}
            >
                {[...displayPills, ...displayPills].map((pill, i) => (
                    <Pill
                        key={pill.key + i}
                        pill={pill}
                        metrics={metrics}
                        shimmer={IS_WEB ? shimmer : null}
                        onLoaded={notePillLoaded}
                    />
                ))}
            </Animated.View>
        </View>
    );
}

const OnboardingScreen = ({ navigation }) => {
    const insets = useSafeAreaInsets();
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const compactBelowCarousel = windowHeight <= 740 || windowWidth <= 380;
    const veryCompactBelowCarousel = windowHeight <= 640 || windowWidth <= 340;

    // Aquece o ranking enquanto o usuário lê a tela. Só o ranking: os prefetches
    // de comunidade e desafios que existiam aqui usavam queryKey que nenhuma
    // tela lê (['community','posts'] vs ['community','posts','infinite',{...}])
    // e ainda rodavam sem sessão, batendo em RLS. Foram removidos.
    useEffect(() => {
        const timer = setTimeout(() => {
            queryClient.prefetchQuery({
                queryKey: rankingKeys.globalRanking(),
                queryFn: () => rankingService.getGlobalRanking(),
                staleTime: 45 * 60 * 1000,
            });
        }, 1200);
        return () => clearTimeout(timer);
    }, []);

    const go = useCallback(
        (mode) => navigation.navigate('UserAuth', { mode }),
        [navigation]
    );

    const handleSignUp = useCallback(() => go('register'), [go]);
    const handleLogin = useCallback(() => go('login'), [go]);

    const highlights = useMemo(
        () =>
            HIGHLIGHTS.map((item) => (
                <View
                    key={item.key}
                    style={[
                        styles.card,
                        compactBelowCarousel && styles.cardCompact,
                        veryCompactBelowCarousel && styles.cardVeryCompact,
                    ]}
                >
                    <Ionicons name={item.icon} size={compactBelowCarousel ? 18 : 20} color={COLORS.yellow} />
                    <Text
                        style={[
                            styles.cardTitle,
                            compactBelowCarousel && styles.cardTitleCompact,
                            veryCompactBelowCarousel && styles.cardTitleVeryCompact,
                        ]}
                        numberOfLines={compactBelowCarousel ? 1 : 2}
                        adjustsFontSizeToFit
                        minimumFontScale={0.82}
                    >
                        {compactBelowCarousel ? item.compactTitle : item.title}
                    </Text>
                    <Text
                        style={[
                            styles.cardCaption,
                            compactBelowCarousel && styles.cardCaptionCompact,
                            veryCompactBelowCarousel && styles.cardCaptionVeryCompact,
                        ]}
                        numberOfLines={veryCompactBelowCarousel ? 1 : 2}
                    >
                        {item.caption}
                    </Text>
                </View>
            )),
        [compactBelowCarousel, veryCompactBelowCarousel]
    );

    return (
        <View style={[styles.screen, IS_WEB && styles.screenWeb, { paddingTop: insets.top }]}>
            <StatusBar barStyle="light-content" backgroundColor={COLORS.navy} />

            <View style={styles.header}>
                <View style={styles.brand}>
                    {/* Logo com fundo transparente e traço amarelo: vai
                        direto sobre o navy. Sem badge circular — logo
                        amarelo dentro de círculo amarelo desapareceria. */}
                    <Image
                        source={require('../../assets/logo-uniquest.webp')}
                        contentFit="contain"
                        transition={0}
                        style={styles.logo}
                        accessibilityIgnoresInvertColors
                    />
                    <View>
                        <Text style={[styles.brandLine, styles.brandLineUnderline]}>UNIQUEST</Text>
                        <Text style={styles.brandInstitution}>Centro Universitário de João Pessoa</Text>
                    </View>
                </View>
            </View>

            <PhotoMarquee speed={26} />

            {/* `body` absorve toda a sobra vertical e distribui entre os blocos
                (space-between). É o que elimina o buraco no rodapé em tela alta
                sem inventar conteúdo — e sem rolagem. */}
            <View style={[
                styles.body,
                IS_WEB && styles.bodyWeb,
                compactBelowCarousel && styles.bodyCompact,
                veryCompactBelowCarousel && styles.bodyVeryCompact,
            ]}>
                <View style={[
                    styles.block,
                    compactBelowCarousel && styles.blockCompact,
                    veryCompactBelowCarousel && styles.blockVeryCompact,
                ]}>
                    {/* Linhas separadas de propósito: `E você?` não pode ser
                        dividido pelo motor de quebra do RN/RNW. */}
                    <View style={styles.headlineBlock}>
                        <Text style={[
                            styles.headline,
                            compactBelowCarousel && styles.headlineCompact,
                            veryCompactBelowCarousel && styles.headlineVeryCompact,
                        ]}>
                            Sua turma está
                        </Text>
                        <Text style={[
                            styles.headline,
                            compactBelowCarousel && styles.headlineCompact,
                            veryCompactBelowCarousel && styles.headlineVeryCompact,
                        ]}>
                            se preparando.
                        </Text>
                        <Text
                            style={[
                                styles.headline,
                                styles.headlineAccent,
                                compactBelowCarousel && styles.headlineCompact,
                                veryCompactBelowCarousel && styles.headlineVeryCompact,
                            ]}
                            numberOfLines={1}
                            adjustsFontSizeToFit
                            minimumFontScale={0.82}
                        >
                            {'E\u00A0você?'}
                        </Text>
                    </View>
                    <Text style={[
                        styles.sub,
                        compactBelowCarousel && styles.subCompact,
                        veryCompactBelowCarousel && styles.subVeryCompact,
                    ]}>
                        Questões, simulados e acompanhamento de desempenho — no ritmo do seu curso.
                    </Text>

                    <View style={[
                        styles.buttons,
                        compactBelowCarousel && styles.buttonsCompact,
                        veryCompactBelowCarousel && styles.buttonsVeryCompact,
                    ]}>
                        <Pressable
                            style={[
                                styles.btn,
                                compactBelowCarousel && styles.btnCompact,
                                veryCompactBelowCarousel && styles.btnVeryCompact,
                                styles.btnPrimary,
                            ]}
                            onPress={handleSignUp}
                            accessibilityRole="button"
                            accessibilityLabel="Criar conta"
                        >
                            <Text style={[styles.btnText, styles.btnTextPrimary]}>Criar conta</Text>
                        </Pressable>
                        <Pressable
                            style={[
                                styles.btn,
                                compactBelowCarousel && styles.btnCompact,
                                veryCompactBelowCarousel && styles.btnVeryCompact,
                                styles.btnSecondary,
                            ]}
                            onPress={handleLogin}
                            accessibilityRole="button"
                            accessibilityLabel="Entrar na sua conta"
                        >
                            <Text style={styles.btnText}>Entrar</Text>
                        </Pressable>
                    </View>
                </View>

                <View style={[
                    styles.blockTight,
                    compactBelowCarousel && styles.blockTightCompact,
                    veryCompactBelowCarousel && styles.blockTightVeryCompact,
                ]}>
                    <Text style={[
                        styles.h2,
                        compactBelowCarousel && styles.h2Compact,
                        veryCompactBelowCarousel && styles.h2VeryCompact,
                    ]}>O que você treina aqui:</Text>
                    <View style={[
                        styles.cards,
                        compactBelowCarousel && styles.cardsCompact,
                        veryCompactBelowCarousel && styles.cardsVeryCompact,
                    ]}>{highlights}</View>
                </View>

                {/* Rodapé: fecha a tela com o ganho concreto de usar o app, sem
                    prometer nota nem citar volume de conteúdo (nada que a gente
                    não consiga sustentar). Nada de link legal aqui — a política
                    de privacidade do UniQuest ainda não foi publicada (a URL
                    existente é a do QuizFlow). Ver manual-actions.md §227. */}
                <View style={[
                    styles.footer,
                    compactBelowCarousel && styles.footerCompact,
                    veryCompactBelowCarousel && styles.footerVeryCompact,
                    { paddingBottom: insets.bottom + (veryCompactBelowCarousel ? 10 : compactBelowCarousel ? 12 : 16) },
                ]}>
                    <Text style={[
                        styles.footerText,
                        compactBelowCarousel && styles.footerTextCompact,
                    ]}>
                        Chegue no dia da prova{' '}
                        <Text style={styles.footerStrong}>sem surpresas</Text>
                    </Text>
                    <Text style={[
                        styles.footerCaption,
                        compactBelowCarousel && styles.footerCaptionCompact,
                        veryCompactBelowCarousel && styles.footerCaptionVeryCompact,
                    ]}>
                        Você já vai ter visto o formato, o tempo e o tipo de questão
                    </Text>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    screen: { flex: 1, backgroundColor: COLORS.navy },

    // WEB — válvula de escape. O layout abaixo já se adapta sozinho (a faixa
    // encolhe e as fotos escalam junto), e na faixa realista de celular isso
    // basta: em 430×745 (iPhone 15 Pro Max no Safari) tudo cabe sem rolagem.
    // Mas existe um piso: num viewport de 375×560 (aparelho pequeno + barra do
    // navegador) só o conteúdo — header ~59 + corpo ~530 — já dá 589px. Nem com
    // a faixa em altura ZERO caberia. Sem rolagem, cards e rodapé ficariam
    // cortados E inalcançáveis, que é o defeito relatado no iPhone.
    // `overflow: 'scroll'` resolve sem tocar no resto: o container continua com
    // a altura da viewport (então o flex segue encolhendo a faixa normalmente),
    // e só o que sobra vira rolagem. Não é um ScrollView — trocar o container
    // desligaria o `flexShrink` da faixa, porque dentro de um ScrollView a
    // altura deixa de ser limitada e nada mais teria motivo para encolher.
    screenWeb: { overflow: 'scroll' },

    // ⚠️ ESTA TELA NÃO ROLA (requisito de produto). Sem ScrollView, a única
    // proteção contra conteúdo cortado é o layout se adaptar sozinho:
    //  - `body` cresce e distribui a sobra entre os blocos (tela alta);
    //  - a faixa de fotos é a ÚNICA que encolhe (`flexShrink` em `strip`), então
    //    numa tela baixa o que comprime é a decoração, nunca os botões.
    // Ao adicionar conteúdo aqui, manter essa hierarquia — qualquer bloco novo
    // com altura fixa reintroduz o risco de corte.
    body: { flex: 1, justifyContent: 'space-between' },
    bodyCompact: { justifyContent: 'flex-start' },
    bodyVeryCompact: { justifyContent: 'flex-start' },

    // WEB — conserta o amortecedor descrito acima, que em navegador nunca
    // disparava. Motivo: `flex: 1` expande para `flex: 1 1 0%`, ou seja base
    // ZERO. Com base zero, o contêiner raiz soma header (~59) + faixa (406) +
    // body (0) = ~465 e conclui que SOBRA espaço — então nunca pede à faixa
    // que encolha. O estouro real (~90px de cards + rodapé) acontece DENTRO do
    // body e é cortado pelo `body { overflow: hidden }` do reset do Expo, sem
    // scroll para resgatar.
    //
    // `flexBasis: 'auto'` faz o body declarar a altura real do seu conteúdo, o
    // déficit finalmente aparece no passe de flex de fora, e `flexShrink: 0`
    // garante que quem cede seja SÓ a faixa de fotos (ela tem ~220px de folga
    // até o `minHeight`) — exatamente a hierarquia que o comentário do `body`
    // define: comprime a decoração, nunca os botões.
    //
    // Não usar ScrollView aqui: além de contrariar o requisito de produto,
    // dentro de um ScrollView a altura deixa de ser limitada e a faixa voltaria
    // a não encolher — o problema só trocaria de forma.
    bodyWeb: { flexGrow: 1, flexShrink: 0, flexBasis: 'auto' },

    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 22,
        paddingTop: 18,
    },
    brand: { flexDirection: 'row', alignItems: 'center', gap: 7 },
    // O asset foi recortado no bbox do traço (o PNG original tinha 16,7% de
    // margem transparente de cada lado). Sem esse recorte, o `gap` acima mentia:
    // sobrava ~6px de vazio dentro da própria imagem. Proporção 104:114 do
    // recorte — mexer só num dos lados distorce.
    logo: { width: 35, height: 38 },
    brandLine: {
        color: COLORS.white,
        fontFamily: FONTS.bold,
        fontSize: 15,
        letterSpacing: 1.5,
        lineHeight: 18,
    },
    brandLineUnderline: {
        borderBottomWidth: 2,
        borderBottomColor: COLORS.white,
        paddingBottom: 3,
        alignSelf: 'flex-start',
    },
    // Assinatura institucional: leve e em caixa mista, de propósito em contraste
    // com a marca acima. Sem `letterSpacing` de 1.5 e num corpo menor porque a
    // linha é longa (35 caracteres) — no espaçamento da marca ela não caberia
    // na largura do cabeçalho.
    brandInstitution: {
        color: 'rgba(255,255,255,0.78)',
        fontFamily: FONTS.light,
        fontSize: 11,
        letterSpacing: 0.2,
        lineHeight: 14,
        marginTop: 4,
    },

    // A faixa cresce SHADOW_PAD para cada lado (para caber a sombra) e devolve o
    // espaço com margens negativas — o conteúdo ao redor não se move. O
    // paddingTop do track recoloca as cápsulas na mesma altura de antes.
    strip: {
        height: STRIP_TOTAL,
        // A altura real é substituída em runtime por `metrics.stripTotal`: o
        // carrossel escala pela largura para manter no máximo 4 fotos visíveis.
        // Em tela baixa, os blocos abaixo compactam primeiro.
        flexShrink: 1,
        minHeight: STRIP_H * 0.55,
        marginTop: 12 - SHADOW_PAD,
        marginBottom: -SHADOW_PAD,
        overflow: 'hidden',
    },
    track: { flexDirection: 'row', gap: PILL_GAP, paddingLeft: 22, paddingTop: SHADOW_PAD },
    // Externa: só sombra. Offset e raio escalam junto com PILL_SCALE para a
    // profundidade acompanhar o tamanho da cápsula. `elevation` fica fixo — no
    // Android ele é uma abstração de profundidade, não uma medida de blur.
    pillShadow: {
        width: PILL_W,
        borderRadius: PILL_RADIUS,
        backgroundColor: COLORS.pillBase,
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: s(10) },
        shadowOpacity: 0.3,
        shadowRadius: s(18),
        elevation: 10,
    },
    // Interna: recorta a foto na cápsula. Nunca mover a sombra para cá.
    pillClip: {
        flex: 1,
        borderRadius: PILL_RADIUS,
        overflow: 'hidden',
        backgroundColor: COLORS.pillBase,
    },

    block: { paddingHorizontal: 22, paddingTop: w(12, 16) },
    blockCompact: { paddingTop: 8 },
    blockVeryCompact: { paddingTop: 6 },
    blockTight: { paddingHorizontal: 22, paddingTop: w(12, 18) },
    blockTightCompact: { paddingTop: 8 },
    blockTightVeryCompact: { paddingTop: 6 },

    headlineBlock: { maxWidth: 340 },
    headline: {
        color: COLORS.white,
        fontFamily: FONTS.bold,
        fontSize: w(30, 34),
        lineHeight: w(34, 38),
        letterSpacing: -1.2,
    },
    headlineCompact: { fontSize: 28, lineHeight: 32 },
    headlineVeryCompact: { fontSize: 26, lineHeight: 30 },
    // ⚠️ `fontFamily` é OBRIGATÓRIO aqui. `AppText` injeta `{fontFamily:
    // 'Liebling'}` como PRIMEIRA entrada do array de estilo em toda instância —
    // inclusive nas aninhadas. Ou seja, um <Text> dentro de outro NÃO herda o
    // 'Liebling-Bold' do pai: a regular injetada ganha, e o trecho sai fino.
    // Vale para qualquer texto aninhado no app, não só aqui.
    // Tamanho/entrelinha/espaçamento continuam herdados — mexer neles aqui
    // desalinharia a linha de base do trecho amarelo.
    headlineAccent: { color: COLORS.yellow, fontFamily: FONTS.heavy },
    sub: {
        color: COLORS.muted,
        fontFamily: FONTS.regular,
        fontSize: w(14, 15),
        lineHeight: w(20, 22),
        marginTop: w(10, 12),
        maxWidth: 320,
    },
    subCompact: { fontSize: 13, lineHeight: 18, marginTop: 8 },
    subVeryCompact: { fontSize: 12.5, lineHeight: 17, marginTop: 6 },

    buttons: { flexDirection: 'row', gap: 12, marginTop: w(14, 18) },
    buttonsCompact: { marginTop: 10 },
    buttonsVeryCompact: { marginTop: 8 },
    btn: { flex: 1, minHeight: w(46, 52), alignItems: 'center', justifyContent: 'center', paddingHorizontal: 8 },
    btnCompact: { minHeight: 44 },
    btnVeryCompact: { minHeight: 40 },
    btnPrimary: {
        backgroundColor: COLORS.yellow,
        borderTopLeftRadius: 26.388,
        borderTopRightRadius: 26.388,
        borderBottomRightRadius: 26.388,
        borderBottomLeftRadius: 0,
    },
    btnSecondary: {
        backgroundColor: COLORS.navyDark,
        borderTopLeftRadius: 26.388,
        borderTopRightRadius: 26.388,
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 26.39,
    },
    btnText: { color: COLORS.white, fontFamily: FONTS.medium, fontSize: 17, letterSpacing: 0.3 },
    btnTextPrimary: { color: COLORS.ink, fontFamily: FONTS.bold },

    h2: { color: COLORS.white, fontFamily: FONTS.bold, fontSize: w(17, 19), letterSpacing: -0.2 },
    h2Compact: { fontSize: 16 },
    h2VeryCompact: { fontSize: 15 },
    cards: { flexDirection: 'row', gap: 10, marginTop: w(10, 14) },
    cardsCompact: { gap: 8, marginTop: 8 },
    cardsVeryCompact: { gap: 6, marginTop: 6 },
    card: {
        flex: 1,
        minHeight: w(84, 96),
        paddingVertical: w(10, 12),
        paddingHorizontal: 10,
        gap: 6,
        justifyContent: 'flex-start',
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.14)',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        borderBottomRightRadius: 20,
        borderBottomLeftRadius: 0,
    },
    cardCompact: { minHeight: 76, paddingVertical: 8, paddingHorizontal: 8, gap: 4 },
    cardVeryCompact: { minHeight: 68, paddingVertical: 7, paddingHorizontal: 7, gap: 3 },
    cardTitle: { color: COLORS.white, fontFamily: FONTS.medium, fontSize: 13, lineHeight: 16 },
    cardTitleCompact: { fontSize: 12, lineHeight: 14 },
    cardTitleVeryCompact: { fontSize: 11, lineHeight: 13 },
    cardCaption: { color: 'rgba(255,255,255,0.55)', fontFamily: FONTS.regular, fontSize: 11, lineHeight: 14 },
    cardCaptionCompact: { fontSize: 10.5, lineHeight: 13 },
    cardCaptionVeryCompact: { fontSize: 10, lineHeight: 12 },

    footer: {
        paddingHorizontal: 22,
        paddingTop: w(10, 16),
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.10)',
    },
    footerCompact: { paddingTop: 8, marginTop: 'auto' },
    footerVeryCompact: { paddingTop: 6, marginTop: 'auto' },
    footerText: {
        color: 'rgba(255,255,255,0.82)',
        fontFamily: FONTS.regular,
        fontSize: 13,
        lineHeight: 17,
        textAlign: 'center',
    },
    footerTextCompact: { fontSize: 12, lineHeight: 16 },
    // fontFamily explícito: AppText injeta a regular em toda instância, então
    // <Text> aninhado não herda peso do pai (mesma pegadinha do headlineAccent).
    footerStrong: { color: COLORS.white, fontFamily: FONTS.bold },
    footerCaption: {
        color: 'rgba(255,255,255,0.45)',
        fontFamily: FONTS.light,
        fontSize: 11,
        lineHeight: 15,
        marginTop: 4,
        textAlign: 'center',
    },
    footerCaptionCompact: { fontSize: 10.5, lineHeight: 13, marginTop: 3 },
    footerCaptionVeryCompact: { fontSize: 10, lineHeight: 12, marginTop: 2 },
});

export default OnboardingScreen;
